/**
 * FinancePage — Finance & Payments Management (2026 redesign)
 *
 * - 5 clickable KPI tiles (click → filtered list view)
 * - Two user-selectable views: kanban board / transactions list
 * - List view: month navigation, status chips, patient search,
 *   therapist filter (admins in multi-therapist clinics)
 * - Income creation modal, payment detail modal, Morning / Digital
 *   Invoice integration — all flows unchanged
 *
 * Error Codes: CK-3500 - CK-3599
 */

import { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  useVisibilityRefresh,
  useCombinedRefetch,
  DEFAULT_POLLING_INTERVAL,
} from "../../hooks/useRealtimeQuery";
// Grow: onSnapshot on active payment links → invalidates finance caches when a patient pays (Phase 3.6/5.2)
import useGrowLinksRealtime from "../../hooks/useGrowLinksRealtime";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistName,
  selectClinicProfile,
  selectCanAccessFinance,
  selectIsAdmin,
  selectHasPermission,
} from "../../store/slices/authSlice";
import { isAssignableTherapist } from "../../config/permissionsRegistry";
import {
  useGetMonthlyTransactionStatsQuery,
  useGetTransactionsForKanbanQuery,
  useGetTransactionsInRangeQuery,
  useGetTransactionByIdQuery,
  useUpdateTransactionStatusMutation,
} from "../../store/services/financeApi";
import { useGetClinicTherapistsQuery } from "../../store/services/therapistApi";
import { TRANSACTION_STATUS } from "../../firebase";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CurrencyCircleDollar, Plus } from "@phosphor-icons/react";
import {
  IncomeModal,
  PaymentKanban,
  PaymentDetailModal,
  FinanceViewTabs,
  FinanceStatTiles,
  FinanceListFilters,
  FinanceTransactionsList,
} from "../../components/finance";
import { getStatusBucket } from "../../components/finance/FinanceTransactionsList";
import InvoiceQuickModal from "../../components/morning/InvoiceQuickModal";
import DigitalInvoiceQuickModal from "../../components/digitalInvoice/DigitalInvoiceQuickModal";
import { HelpButton, PageHeader } from "../../components/shared";
import CustomClinikitActionButton from "../../components/ui/CustomClinikitActionButton";
import "./FinancePage.css";

const VIEW_STORAGE_KEY = "clinikit_finance_view";

// Stable empty array so memoized consumers don't re-run every render.
const EMPTY_ITEMS = [];

function FinancePage() {
  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);
  const clinicProfile = useSelector(selectClinicProfile);
  const canAccessFinance = useSelector(selectCanAccessFinance);
  const isAdmin = useSelector(selectIsAdmin);
  // Permissions v2: clinic-wide finance scope (admin, or finance.viewClinicWide)
  const canViewClinicWide = useSelector(selectHasPermission("finance.viewClinicWide"));
  const hasClinicWideScope = isAdmin || canViewClinicWide;
  const navigate = useNavigate();

  const allowFinanceVal = clinicProfile?.therapistPermissions?.allowFinanceAccess;
  // Admins: legacy rule unchanged (clinic-wide tiles/kanban only when
  // therapists have no finance access of their own). Non-admins with
  // finance.viewClinicWide (e.g. a secretary): tiles + kanban follow the
  // clinic-wide list instead of staying own-scoped (empty for a secretary).
  const includeAllTherapists = isAdmin
    ? allowFinanceVal === false || allowFinanceVal === null
    : canViewClinicWide === true;

  useEffect(() => {
    if (!canAccessFinance) navigate("/dashboard", { replace: true });
  }, [canAccessFinance, navigate]);

  const isDigitalInvoiceActive = clinicProfile?.isDigitalInvoiceIntegrated === true;

  // ===== View selection (kanban / list) =====
  // Saved preference wins; otherwise list is the default everywhere.
  // Only an explicit tab click is persisted.
  const [view, setView] = useState(() => {
    const saved = localStorage.getItem(VIEW_STORAGE_KEY);
    if (saved === "kanban" || saved === "list") return saved;
    return "list";
  });

  const handleViewChange = (nextView) => {
    setView(nextView);
    localStorage.setItem(VIEW_STORAGE_KEY, nextView);
  };

  // ===== List month navigation =====
  const nowDate = new Date();
  const [listMonth, setListMonth] = useState({
    year: nowDate.getFullYear(),
    month: nowDate.getMonth() + 1, // 1-12
  });

  const goMonth = (delta) => {
    setListMonth((prev) => {
      const d = new Date(prev.year, prev.month - 1 + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() + 1 };
    });
  };

  const monthLabel = new Date(listMonth.year, listMonth.month - 1, 1)
    .toLocaleDateString("he-IL", { month: "long", year: "numeric" });

  // ===== List filters =====
  const [activeStatuses, setActiveStatuses] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [therapistFilterId, setTherapistFilterId] = useState(""); // "" = all (admins)

  const toggleStatus = (key) => {
    setActiveStatuses((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  // Therapist filter — admins (or finance.viewClinicWide) in multi-therapist clinics only
  const showTherapistFilter = hasClinicWideScope && !clinicProfile?.isSoloClinic;
  const { data: clinicTherapists = [] } = useGetClinicTherapistsQuery(
    { clinicId },
    { skip: !clinicId || !showTherapistFilter }
  );
  const activeTherapists = useMemo(
    () => clinicTherapists.filter(isAssignableTherapist),
    [clinicTherapists]
  );

  // Clinic-wide scope sees all therapists by default; otherwise own-scoped.
  const rangeTherapistId = hasClinicWideScope
    ? therapistFilterId || undefined
    : therapistId;

  // ===== Stats (current month — tiles always reflect "this month") =====
  const statsYear = nowDate.getFullYear();
  const statsMonth = nowDate.getMonth() + 1;

  // Modal states
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showPaymentDetail, setShowPaymentDetail] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoicePayment, setInvoicePayment] = useState(null);

  const {
    data: transactionStats,
    isLoading: statsLoading,
    refetch: refetchStats,
  } = useGetMonthlyTransactionStatsQuery(
    { clinicId, therapistId, year: statsYear, month: statsMonth, includeAllTherapists },
    {
      skip: !clinicId || !therapistId,
      pollingInterval: DEFAULT_POLLING_INTERVAL,
    }
  );

  // Kanban buckets — used for the "future payments" / "invoices" tiles in
  // both views (PaymentKanban mounts its own copy of this query; RTKQ dedupes)
  const {
    data: kanbanData,
    refetch: refetchKanban,
  } = useGetTransactionsForKanbanQuery(
    { clinicId, therapistId, includeAllTherapists },
    {
      skip: !clinicId || !therapistId,
      pollingInterval: DEFAULT_POLLING_INTERVAL,
    }
  );

  // List data — month window via the Reports-proven range query
  const rangeStart = useMemo(
    () => new Date(listMonth.year, listMonth.month - 1, 1).toISOString(),
    [listMonth]
  );
  const rangeEnd = useMemo(
    () => new Date(listMonth.year, listMonth.month, 0, 23, 59, 59, 999).toISOString(),
    [listMonth]
  );

  const {
    data: rangeTransactions = [],
    isLoading: rangeLoading,
    refetch: refetchRange,
  } = useGetTransactionsInRangeQuery(
    { clinicId, start: rangeStart, end: rangeEnd, therapistId: rangeTherapistId },
    {
      skip: !clinicId || !therapistId || view !== "list",
      pollingInterval: DEFAULT_POLLING_INTERVAL,
    }
  );

  const [updateTransactionStatus] = useUpdateTransactionStatusMutation();

  const combinedRefetch = useCombinedRefetch(
    view === "list"
      ? [refetchStats, refetchKanban, refetchRange]
      : [refetchStats, refetchKanban]
  );
  useVisibilityRefresh(combinedRefetch);
  useGrowLinksRealtime();

  // ===== `?tx=<id>` deep link (Grow "payment received" notifications, Phase 5.8) =====
  // Opens PaymentDetailModal for that transaction once, then strips the param so a
  // later close / refresh does not reopen it. Looks in the loaded Kanban buckets
  // first; falls back to a by-id fetch for transactions outside the current view.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkTxId = searchParams.get("tx") || null;
  const deepLinkFromKanban = useMemo(() => {
    if (!deepLinkTxId || !kanbanData) return null;
    for (const bucket of Object.values(kanbanData)) {
      if (!Array.isArray(bucket)) continue;
      const hit = bucket.find((t) => t?.id === deepLinkTxId);
      if (hit) return hit;
    }
    return null;
  }, [deepLinkTxId, kanbanData]);
  const {
    data: deepLinkFetched,
    isFetching: deepLinkFetching,
    error: deepLinkError,
  } = useGetTransactionByIdQuery(
    { clinicId, transactionId: deepLinkTxId },
    { skip: !clinicId || !deepLinkTxId || !!deepLinkFromKanban || !kanbanData }
  );
  useEffect(() => {
    if (!deepLinkTxId) return;
    const tx = deepLinkFromKanban || deepLinkFetched;
    const clearParam = () => {
      const next = new URLSearchParams(searchParams);
      next.delete("tx");
      setSearchParams(next, { replace: true });
    };
    if (tx) {
      setSelectedPayment(tx);
      setShowPaymentDetail(true);
      clearParam();
    } else if (deepLinkError || (deepLinkFetched === null && !deepLinkFetching && !deepLinkFromKanban && kanbanData)) {
      console.error("[CK-3862] Finance ?tx deep link: transaction not found", {
        errorCode: "CK-3862",
        errorMessage: deepLinkError?.message || "not_found",
        transactionId: deepLinkTxId,
        timestamp: new Date().toISOString(),
      });
      clearParam();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkTxId, deepLinkFromKanban, deepLinkFetched, deepLinkFetching, deepLinkError]);

  // ===== Derived data =====
  const displayStats = useMemo(
    () => ({
      actualIncome: transactionStats?.actualIncome || 0,
      expectedIncome: transactionStats?.expectedIncome || 0,
      pendingCollectionAmount: transactionStats?.pendingCollectionAmount || 0,
      actualItems: transactionStats?.actualItems || EMPTY_ITEMS,
      expectedItems: transactionStats?.expectedItems || EMPTY_ITEMS,
      pendingItems: transactionStats?.pendingItems || EMPTY_ITEMS,
    }),
    [transactionStats]
  );

  // "חשבוניות החודש" — the label says THIS month, so only count invoices
  // issued in the current Israel month (it used to sum the whole invoiced
  // column, growing forever). Date priority: manual invoice date → server
  // issue time → payment date.
  const invoicesSummary = useMemo(() => {
    if (!kanbanData?.invoiced) return { totalAmount: 0, count: 0, items: [] };
    const monthKey = new Date().toLocaleDateString("en-CA", {
      timeZone: "Asia/Jerusalem",
    }).slice(0, 7);
    const toMonthKey = (v) => {
      if (!v) return null;
      const d = v?.toDate ? v.toDate() : new Date(v);
      if (Number.isNaN(d.getTime())) return null;
      return d.toLocaleDateString("en-CA", { timeZone: "Asia/Jerusalem" }).slice(0, 7);
    };
    const thisMonth = kanbanData.invoiced.filter((tx) => {
      const key =
        toMonthKey(tx.manualInvoiceDate) ||
        toMonthKey(tx.invoiceIssuedAt) ||
        toMonthKey(tx.actualPaymentDate);
      return key === monthKey;
    });
    const totalAmount = thisMonth.reduce((sum, tx) => sum + (tx.amount || 0), 0);
    const items = thisMonth.map((tx) => ({
      id: tx.id,
      patientName: tx.patientName || null,
      description: tx.description || null,
      amount: tx.amount || 0,
      date: tx.manualInvoiceDate || tx.invoiceIssuedAt || tx.actualPaymentDate || null,
    }));
    return { totalAmount, count: thisMonth.length, items };
  }, [kanbanData]);

  const futurePayments = useMemo(() => {
    if (!kanbanData?.expected) return { totalAmount: 0, count: 0, items: [] };
    const totalAmount = kanbanData.expected.reduce((sum, tx) => sum + (tx.amount || 0), 0);
    const items = kanbanData.expected.map((tx) => ({
      id: tx.id,
      patientName: tx.patientName || null,
      description: tx.description || null,
      amount: tx.amount || 0,
      date: tx.expectedPaymentDate || null,
    }));
    return { totalAmount, count: kanbanData.expected.length, items };
  }, [kanbanData]);

  // Filtered + sorted list rows
  const filteredTransactions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const rows = rangeTransactions.filter((tx) => {
      if (activeStatuses.length > 0 && !activeStatuses.includes(getStatusBucket(tx.status))) {
        return false;
      }
      if (term) {
        const haystack = `${tx.patientName || ""} ${tx.description || ""}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
    const txTime = (tx) =>
      new Date(tx.actualPaymentDate || tx.expectedPaymentDate || tx.createdAt || 0).getTime();
    rows.sort((a, b) => txTime(b) - txTime(a));
    return rows;
  }, [rangeTransactions, activeStatuses, searchTerm]);

  // ===== Handlers =====
  const handleCardClick = (payment) => {
    setSelectedPayment(payment);
    setShowPaymentDetail(true);
  };

  const handleInvoiceRequest = (payment) => {
    setInvoicePayment(payment);
    setShowInvoiceModal(true);
  };

  const handleInvoiceSuccess = async (invoiceData) => {
    // Update the transaction status to INVOICE_AUTO after successful invoice creation
    if (invoicePayment?.id) {
      try {
        await updateTransactionStatus({
          clinicId,
          transactionId: invoicePayment.id,
          newStatus: TRANSACTION_STATUS.INVOICE_AUTO,
          additionalData: {
            invoiceNumber: invoiceData?.invoiceNumber,
            invoiceUrl: invoiceData?.invoiceUrl,
            invoiceDocumentId: invoiceData?.documentId,
            invoiceProvider: invoiceData?.invoiceProvider || null,
          },
          therapistId,
          therapistName,
          patientName: invoicePayment.patientName,
          patientId: invoicePayment.patientId,
        }).unwrap();
      } catch (error) {
        console.error("[CK-3550] Error updating transaction status after invoice:", error);
      }
    }

    setShowInvoiceModal(false);
    setInvoicePayment(null);
    combinedRefetch();
  };

  if (!canAccessFinance) return null;

  return (
    <div className="finance-page">
      {/* Page Header — .finance-header-actions is an onboarding-tour anchor */}
      <PageHeader
        icon={CurrencyCircleDollar}
        title="פיננסים ותשלומים"
        actions={
          <div className="finance-header-actions">
            <HelpButton featureKey="finance" />
            <CustomClinikitActionButton
              icon={<Plus size={16} weight="bold" />}
              onClick={() => setShowIncomeModal(true)}
            >
              יצירת הכנסה
            </CustomClinikitActionButton>
          </div>
        }
      />

      {/* KPI tiles — click a tile to see what the number is built from */}
      <FinanceStatTiles
        actualIncome={displayStats.actualIncome}
        expectedIncome={displayStats.expectedIncome}
        pendingAmount={displayStats.pendingCollectionAmount}
        futureAmount={futurePayments.totalAmount}
        invoicesAmount={invoicesSummary.totalAmount}
        actualItems={displayStats.actualItems}
        expectedItems={displayStats.expectedItems}
        pendingItems={displayStats.pendingItems}
        futureItems={futurePayments.items}
        invoiceItems={invoicesSummary.items}
        isLoading={statsLoading}
      />

      {/* Board card: view tabs + active view */}
      <div className="fin-board-card">
        <FinanceViewTabs view={view} onViewChange={handleViewChange} />

        {view === "kanban" ? (
          <div className="finance-kanban-section fin-board-body">
            <PaymentKanban
              onCardClick={handleCardClick}
              onInvoiceRequest={handleInvoiceRequest}
              includeAllTherapists={includeAllTherapists}
            />
          </div>
        ) : (
          <div className="fin-board-body">
            <FinanceListFilters
              monthLabel={monthLabel}
              onPrevMonth={() => goMonth(-1)}
              onNextMonth={() => goMonth(1)}
              activeStatuses={activeStatuses}
              onToggleStatus={toggleStatus}
              onClearStatuses={() => setActiveStatuses([])}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              showTherapistFilter={showTherapistFilter && activeTherapists.length > 1}
              therapists={activeTherapists}
              selectedTherapistId={therapistFilterId}
              onTherapistChange={setTherapistFilterId}
            />
            <FinanceTransactionsList
              transactions={filteredTransactions}
              isLoading={rangeLoading}
              onRowClick={handleCardClick}
            />
          </div>
        )}
      </div>

      {/* Income Modal */}
      <IncomeModal
        isOpen={showIncomeModal}
        onClose={() => setShowIncomeModal(false)}
        onSuccess={() => {
          setShowIncomeModal(false);
          combinedRefetch();
        }}
      />

      {/* Payment Detail Modal */}
      <PaymentDetailModal
        transaction={selectedPayment}
        isOpen={showPaymentDetail}
        onClose={() => {
          setShowPaymentDetail(false);
          setSelectedPayment(null);
        }}
        onUpdate={combinedRefetch}
        onInvoiceRequest={handleInvoiceRequest}
      />

      {/* Invoice Quick Modal - prefer Digital Invoice if connected, else Morning */}
      {showInvoiceModal && invoicePayment && (
        isDigitalInvoiceActive ? (
          <DigitalInvoiceQuickModal
            isOpen={showInvoiceModal}
            onClose={() => {
              setShowInvoiceModal(false);
              setInvoicePayment(null);
            }}
            transaction={invoicePayment}
            patientName={invoicePayment.patientName}
            amount={invoicePayment.amount?.toString()}
            paymentMethod={invoicePayment.paymentMethod}
            onSuccess={handleInvoiceSuccess}
          />
        ) : (
          <InvoiceQuickModal
            isOpen={showInvoiceModal}
            onClose={() => {
              setShowInvoiceModal(false);
              setInvoicePayment(null);
            }}
            transaction={invoicePayment}
            patientName={invoicePayment.patientName}
            amount={invoicePayment.amount?.toString()}
            paymentMethod={invoicePayment.paymentMethod}
            onSuccess={handleInvoiceSuccess}
          />
        )
      )}
    </div>
  );
}

export default FinancePage;
